import { ContractReportAccessTypeIcon } from 'app/modules/contracts/common/ContractAccessTypeIcon';
import { constantToDisplayName } from "../core/constantToDisplayName";
import { useFormatUserDate } from "../core/useFormatUserDate";
import { renderProductIcon } from 'legoland-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useFilters, useTable } from 'react-table';
import styled from 'styled-components';
import { Box, Button, Modal, Text, TooltipOnHover, tombac, useToasts, useTombac } from 'tombac';
import { CloneIcon, CopyIcon, ShowIcon } from 'tombac-icons';
import { useCopyReportMutation, useReportsList, useTransferReportMutation } from "./reportsApiHooks";
import { UserRole } from 'legoland-sdk/dist/experimental';
import { StatusDot, StatusDotContainer } from "../core/components/StatusDot/ContractStatusDot";
import { Tab, Tabs } from "../core/components/Tabs/Tabs";
import { Preloader } from 'llui';
import { Pagination } from "../contracts/list/Pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "../contracts/list/Table";
import CopiesList from "./CopiesList";
import { CopyReportsForm } from "./CopyReportsForm";
import { ReportStatusType } from "./reportTypes";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ReportsList = ({
  user,
  creatorRole
}) => {
  const [pagination, setPagination] = useState({
    index: 0,
    size: 20
  });
  const [reportsListInterval, setReportsListInterval] = useState(false);
  const [activeProductTab, setActiveProductTab] = useState('ts');
  const reportListQuery = useReportsList({
    email: encodeURIComponent(user.email),
    pagination,
    product: activeProductTab
  }, {
    refetchInterval: reportsListInterval ? 10000 : false,
    keepPreviousData: true
  });
  const {
    data: reportsList,
    isLoading,
    isFetching
  } = reportListQuery;
  const {
    mutateAsync: copyReport
  } = useCopyReportMutation();
  const {
    mutateAsync: transferReport
  } = useTransferReportMutation();
  const [isCopyReportOpen, setIsCopyReportOpen] = useState(false);
  const [activeReportData, setActiveReportData] = useState(null);
  const tombac = useTombac();
  const {
    addToast
  } = useToasts();
  const tableData = useMemo(() => reportsList?.content ?? [], [reportsList]);
  useEffect(() => {
    const copiesOnly = tableData.filter(el => el.copies.length > 0).reduce((acc, current) => {
      return [...acc, ...current.copies.filter(el => el.status === 'IN_PROGRESS')];
    }, []);
    copiesOnly.length > 0 ? setReportsListInterval(true) : setReportsListInterval(false);
  }, [tableData]);
  const onSubmitCopy = (destUser, shouldTransferReport) => {
    const reportParams = {
      srcId: activeReportData.id,
      destUser: destUser.email,
      srcUser: user.email,
      product: {
        id: activeReportData.product.id
      }
    };
    const functionParams = {
      onError: async error => {
        console.error(error);
        const response = await error.json();
        addToast(response.message ? /*#__PURE__*/_jsx("div", {
          style: {
            overflowWrap: 'anywhere',
            paddingRight: '5px'
          },
          children: response.message
        }) : `Failed to request ${shouldTransferReport ? 'move' : 'copy'} for the data`, 'danger');
      },
      onSuccess() {
        addToast(`Data has been requested to ${shouldTransferReport ? 'move' : 'copy'}`, 'success');
        setIsCopyReportOpen(false);
      }
    };
    shouldTransferReport ? transferReport(reportParams, functionParams) : copyReport(reportParams, functionParams);
  };
  const reportStatusColorsMap = {
    [ReportStatusType.Done]: 'success',
    [ReportStatusType.InProgress]: 'alert',
    [ReportStatusType.Error]: 'neutral'
  };
  const checkButtonStatus = (isCopying, isCopyable) => {
    if (!isCopyable) {
      return "You cannot copy this report because it's either expired, deleted or cancelled";
    } else if (isCopyable && isCopying) {
      return 'Copying in progress';
    } else return 'Copy report';
  };
  const columns = useMemo(() => [{
    Header: 'Name',
    accessor: 'name',
    Cell: ({
      value
    }) => /*#__PURE__*/_jsx(ReportName, {
      children: value || 'Unnamed report'
    })
  }, {
    Header: 'ID',
    accessor: 'id'
  }, {
    Header: 'Access type',
    accessor: 'accessType',
    Cell: ({
      value
    }) => /*#__PURE__*/_jsx(TooltipOnHover, {
      content: constantToDisplayName(value ? value : ''),
      placement: "top",
      size: "s",
      children: value && /*#__PURE__*/_jsx(ContractReportAccessTypeIcon, {
        accessType: value
      })
    }),
    width: 55
  }, {
    Header: 'App',
    accessor: 'product',
    Cell: ({
      value
    }) => renderProductIcon(value.id),
    width: 55
  }, {
    Header: 'Created at',
    accessor: 'createdAt',
    width: 90,
    Cell: ({
      value
    }) => {
      const formatDateTime = useFormatUserDate({
        withTime: true
      });
      return /*#__PURE__*/_jsx(_Fragment, {
        children: formatDateTime(new Date(value))
      });
    }
  }, {
    id: 'actions',
    width: 80,
    Cell: ({
      row
    }) => {
      const isCopying = row.original.copies.filter(el => el.status === 'IN_PROGRESS').length > 0;
      const hasCopies = row.original.copies.length > 0;
      const sortedReports = row.original.copies.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const colorScheme = sortedReports.length > 0 ? reportStatusColorsMap[sortedReports[0].status] : undefined;
      const isCopyable = row.original.isCopyable;
      const showCopyButton = creatorRole === UserRole.Admin && (row.original.product.id === 'od' || row.original.product.id === 'ts' || row.original.product.id === 'aa');
      const showTransferButton = creatorRole === UserRole.Admin && (row.original.product.id === 'ja' || row.original.product.id === 'ctm');
      const buttonStatusLabel = checkButtonStatus(isCopying, isCopyable);
      const reportPreviewUrl = row.original.url && (UserRole.Admin || UserRole.Support) ? row.original.url : '';
      return /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        children: [showCopyButton && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(TooltipOnHover, {
            content: buttonStatusLabel,
            placement: "top",
            size: "s",
            children: /*#__PURE__*/_jsx(CopyButton, {
              shape: "circle",
              variant: "flat",
              disabled: isCopying || !isCopyable,
              ...(!isCopying && {
                onClick: () => {
                  setActiveReportData(row.original);
                  setIsCopyReportOpen(true);
                }
              }),
              children: /*#__PURE__*/_jsx(CopyIcon, {})
            })
          }), hasCopies && /*#__PURE__*/_jsx(TooltipOnHover, {
            content: /*#__PURE__*/_jsx(CopiesList, {
              colorMapping: reportStatusColorsMap,
              copies: row.original.copies
            }),
            placement: "top",
            size: "s",
            children: /*#__PURE__*/_jsx(Button, {
              $ml: "1sp",
              shape: "circle",
              variant: "flat",
              onClick: () => {},
              children: /*#__PURE__*/_jsx(StatusDotContainer, {
                colorScheme: colorScheme,
                children: /*#__PURE__*/_jsx(StatusDot, {
                  colorScheme: colorScheme
                })
              })
            })
          })]
        }), showTransferButton && /*#__PURE__*/_jsx(TooltipOnHover, {
          content: 'Move report',
          placement: "top",
          size: "s",
          children: /*#__PURE__*/_jsx(CopyButton, {
            shape: "circle",
            variant: "flat",
            onClick: () => {
              setActiveReportData(row.original);
              setIsCopyReportOpen(true);
            },
            children: /*#__PURE__*/_jsx(CloneIcon, {})
          })
        }), reportPreviewUrl && /*#__PURE__*/_jsx(TooltipOnHover, {
          content: "Show report in a new tab",
          placement: "top",
          size: "s",
          children: /*#__PURE__*/_jsx(Button, {
            $ml: "1sp",
            shape: "circle",
            variant: "flat",
            as: "a",
            href: reportPreviewUrl,
            target: "_blank",
            children: /*#__PURE__*/_jsx(ShowIcon, {})
          })
        })]
      });
    }
  }], []);
  const onPageChange = page => {
    setPagination(page);
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page
  } = useTable({
    columns: columns,
    data: tableData,
    autoResetPage: false
  }, useFilters);
  const ReportsTable = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Box, {
      $display: "flex",
      $alignItems: "center",
      $mb: "2sp",
      $height: "50px",
      $justifyContent: "end",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "ReportsList.tsx",
      "data-sentry-element": "Box",
      children: [(isLoading || isFetching) && /*#__PURE__*/_jsx(Preloader, {}), /*#__PURE__*/_jsx(Pagination, {
        $ml: "2sp",
        page: pagination,
        itemCount: reportsList?.totalElements,
        onPageChange: page => onPageChange && onPageChange(page),
        "data-sentry-element": "Pagination",
        "data-sentry-source-file": "ReportsList.tsx",
        "data-sentry-element": "Pagination"
      })]
    }), tableData.length === 0 ? /*#__PURE__*/_jsx(Box, {
      $display: "flex",
      $justifyContent: "center",
      $mt: "3sp",
      children: /*#__PURE__*/_jsx(Text, {
        variant: "secondary",
        children: "No reports"
      })
    }) : /*#__PURE__*/_jsxs(Table, {
      ...getTableProps(),
      children: [/*#__PURE__*/_jsx(Thead, {
        children: headerGroups.map(headerGroup => /*#__PURE__*/_jsx(Tr, {
          ...headerGroup.getHeaderGroupProps(),
          children: headerGroup.headers.map(column => /*#__PURE__*/_jsx(Th, {
            ...column.getHeaderProps({
              style: {
                width: tombac.unit(Number(column.width))
              }
            }),
            children: column.render('Header')
          }))
        }))
      }), /*#__PURE__*/_jsx(Tbody, {
        ...getTableBodyProps(),
        children: rows.map(row => {
          prepareRow(row);
          return /*#__PURE__*/_jsx(Tr, {
            ...row.getRowProps(),
            $height: "64u",
            children: row.cells.map(cell => {
              return /*#__PURE__*/_jsx(Td, {
                $overflow: "hidden",
                $whiteSpace: "nowrap",
                ...cell.getCellProps(),
                children: cell.render('Cell')
              });
            })
          });
        })
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Box, {
      $boxSizing: "content-box",
      $margin: "1sp auto",
      $width: "100%",
      "data-sentry-element": "Box",
      "data-sentry-source-file": "ReportsList.tsx",
      "data-sentry-element": "Box",
      children: /*#__PURE__*/_jsxs(StyledTabs, {
        "data-sentry-element": "StyledTabs",
        "data-sentry-source-file": "ReportsList.tsx",
        "data-sentry-element": "StyledTabs",
        children: [/*#__PURE__*/_jsx(Tab, {
          onChangeTab: () => {
            setActiveProductTab('ts');
          },
          tabContent: /*#__PURE__*/_jsxs(TabElementWithIcon, {
            children: [renderProductIcon('ts'), " TS"]
          }),
          "data-sentry-element": "Tab",
          "data-sentry-source-file": "ReportsList.tsx",
          "data-sentry-element": "Tab",
          children: /*#__PURE__*/_jsx(ReportsTable, {
            "data-sentry-element": "ReportsTable",
            "data-sentry-source-file": "ReportsList.tsx",
            "data-sentry-element": "ReportsTable"
          })
        }), /*#__PURE__*/_jsx(Tab, {
          onChangeTab: () => {
            setActiveProductTab('od');
          },
          tabContent: /*#__PURE__*/_jsxs(TabElementWithIcon, {
            children: [renderProductIcon('od'), " O/D"]
          }),
          "data-sentry-element": "Tab",
          "data-sentry-source-file": "ReportsList.tsx",
          "data-sentry-element": "Tab",
          children: /*#__PURE__*/_jsx(ReportsTable, {
            "data-sentry-element": "ReportsTable",
            "data-sentry-source-file": "ReportsList.tsx",
            "data-sentry-element": "ReportsTable"
          })
        }), /*#__PURE__*/_jsx(Tab, {
          onChangeTab: () => {
            setActiveProductTab('ja');
          },
          tabContent: /*#__PURE__*/_jsxs(TabElementWithIcon, {
            children: [renderProductIcon('ja'), " JA"]
          }),
          "data-sentry-element": "Tab",
          "data-sentry-source-file": "ReportsList.tsx",
          "data-sentry-element": "Tab",
          children: /*#__PURE__*/_jsx(ReportsTable, {
            "data-sentry-element": "ReportsTable",
            "data-sentry-source-file": "ReportsList.tsx",
            "data-sentry-element": "ReportsTable"
          })
        }), /*#__PURE__*/_jsx(Tab, {
          onChangeTab: () => {
            setActiveProductTab('ctm');
          },
          tabContent: /*#__PURE__*/_jsxs(TabElementWithIcon, {
            children: [renderProductIcon('ctm'), " RM"]
          }),
          "data-sentry-element": "Tab",
          "data-sentry-source-file": "ReportsList.tsx",
          "data-sentry-element": "Tab",
          children: /*#__PURE__*/_jsx(ReportsTable, {
            "data-sentry-element": "ReportsTable",
            "data-sentry-source-file": "ReportsList.tsx",
            "data-sentry-element": "ReportsTable"
          })
        }), /*#__PURE__*/_jsx(Tab, {
          onChangeTab: () => {
            setActiveProductTab('aa');
          },
          tabContent: /*#__PURE__*/_jsxs(TabElementWithIcon, {
            children: [renderProductIcon('aa'), " AA"]
          }),
          "data-sentry-element": "Tab",
          "data-sentry-source-file": "ReportsList.tsx",
          "data-sentry-element": "Tab",
          children: /*#__PURE__*/_jsx(ReportsTable, {
            "data-sentry-element": "ReportsTable",
            "data-sentry-source-file": "ReportsList.tsx",
            "data-sentry-element": "ReportsTable"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(Modal, {
      isOpen: isCopyReportOpen,
      "data-sentry-element": "Modal",
      "data-sentry-source-file": "ReportsList.tsx",
      "data-sentry-element": "Modal",
      children: /*#__PURE__*/_jsx(CopyReportsForm, {
        report: activeReportData,
        onSubmitCopy: onSubmitCopy,
        onCancel: () => {
          setIsCopyReportOpen(false);
          setActiveReportData(null);
        },
        "data-sentry-element": "CopyReportsForm",
        "data-sentry-source-file": "ReportsList.tsx",
        "data-sentry-element": "CopyReportsForm"
      })
    })]
  });
};
const ReportName = styled.div.withConfig({
  displayName: "ReportsList__ReportName"
})(["", ""], tombac.text({
  fontWeight: 500
}));
const CopyButton = styled(Button).withConfig({
  displayName: "ReportsList__CopyButton"
})(["", " &:disabled{cursor:not-allowed;}"], tombac.text({
  fontWeight: 500
}));
const TabElementWithIcon = styled.div.withConfig({
  displayName: "ReportsList__TabElementWithIcon"
})(["display:flex;align-items:center;svg{width:30px;margin-right:4px;}"]);
const StyledTabs = styled(Tabs).withConfig({
  displayName: "ReportsList__StyledTabs"
})([".Tabs_nav{display:inline-flex;}"]);